import { useStorage } from '@vueuse/core'
import { computed } from 'vue'

const CURRENCY_MAP: Record<string, string> = {
    // Europe
    'en-GB': 'GBP', 'en-IE': 'EUR', 'de-DE': 'EUR', 'fr-FR': 'EUR', 'it-IT': 'EUR',
    'es-ES': 'EUR', 'pt-PT': 'EUR', 'nl-NL': 'EUR', 'el-GR': 'EUR', 'pl-PL': 'PLN',
    'sv-SE': 'SEK', 'da-DK': 'DKK', 'no-NO': 'NOK', 'fi-FI': 'EUR', 'cs-CZ': 'CZK',
    'hu-HU': 'HUF', 'ro-RO': 'RON', 'bg-BG': 'BGN', 'hr-HR': 'EUR', 'sk-SK': 'EUR',
    'de-CH': 'CHF', 'fr-CH': 'CHF', 'it-CH': 'CHF',

    // Asia & Pacific
    'ja-JP': 'JPY', 'ko-KR': 'KRW', 'zh-CN': 'CNY', 'zh-TW': 'TWD', 'zh-HK': 'HKD',
    'en-IN': 'INR', 'hi-IN': 'INR', 'id-ID': 'IDR', 'ms-MY': 'MYR', 'en-SG': 'SGD',
    'th-TH': 'THB', 'vi-VN': 'VND', 'en-PH': 'PHP', 'tl-PH': 'PHP',
    'en-AU': 'AUD', 'en-NZ': 'NZD',

    // Americas
    'en-US': 'USD', 'en-CA': 'CAD', 'fr-CA': 'CAD', 'es-MX': 'MXN', 'pt-BR': 'BRL',
    'es-AR': 'ARS', 'es-CL': 'CLP', 'es-CO': 'COP', 'es-PE': 'PEN', 'es-VE': 'VES',

    // Africa & Middle East
    'en-ZA': 'ZAR', 'ar-EG': 'EGP', 'ar-MA': 'MAD', 'en-NG': 'NGN', 'en-KE': 'KES',
    'ar-SA': 'SAR', 'ar-AE': 'AED', 'he-IL': 'ILS', 'tr-TR': 'TRY'
}

const AVAILABLE_CURRENCIES = ref([
    { code: 'USD', label: 'US Dollar ($)', icon: 'i-circle-flags-us' },
    { code: 'EUR', label: 'Euro (€)', icon: 'i-circle-flags-eu' },
    { code: 'GBP', label: 'British Pound (£)', icon: 'i-circle-flags-gb' },
    { code: 'JPY', label: 'Japanese Yen (¥)', icon: 'i-circle-flags-jp' },
    { code: 'CAD', label: 'Canadian Dollar ($)', icon: 'i-circle-flags-ca' },
    { code: 'AUD', label: 'Australian Dollar ($)', icon: 'i-circle-flags-au' },
    { code: 'PHP', label: 'Philippine Peso (₱)', icon: 'i-circle-flags-ph' },
    { code: 'INR', label: 'Indian Rupee (₹)', icon: 'i-circle-flags-in' },
    { code: 'ZAR', label: 'South African Rand (R)', icon: 'i-circle-flags-za' },
    { code: 'MXN', label: 'Mexican Peso ($)', icon: 'i-circle-flags-mx' },
    { code: 'BRL', label: 'Brazilian Real (R$)', icon: 'i-circle-flags-br' }
])

export function useCurrency() {
    // Detect closest locale or default to USD
    const getBrowserCurrency = () => {
        if (typeof navigator !== 'undefined') {
            const languages = navigator.languages || [navigator.language]
            for (const lang of languages) {
                if (CURRENCY_MAP[lang]) return CURRENCY_MAP[lang]

                // Try fallback to generic language prefix
                const prefix = lang.split('-')[0]
                if (!prefix) continue;
                const fallback = Object.keys(CURRENCY_MAP).find(k => k.startsWith(prefix))
                if (fallback && CURRENCY_MAP[fallback]) return CURRENCY_MAP[fallback]
            }
        }
        return 'USD'
    }

    const preferredCurrency = useStorage('claimready-currency', getBrowserCurrency())

    const formatCurrency = (value: number | string | undefined | null) => {
        if (value === undefined || value === null || isNaN(Number(value))) return ''
        const val = Number(value)

        try {
            const localeStr = (typeof navigator !== 'undefined' ? navigator.language : 'en-US') || 'en-US'
            const currencyStr = preferredCurrency.value ? String(preferredCurrency.value) : 'USD'
            return new Intl.NumberFormat(localeStr, {
                style: 'currency',
                currency: currencyStr,
                minimumFractionDigits: val % 1 !== 0 ? 2 : 0, // Drop .00 if whole number
                maximumFractionDigits: 2
            }).format(val)
        } catch {
            // Fallback if the environment does not support the currency code properly
            return `$${val.toLocaleString()}`
        }
    }

    return {
        preferredCurrency,
        formatCurrency,
        AVAILABLE_CURRENCIES
    }
}
